import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Heading, VStack, HStack, Button, Badge } from '@chakra-ui/react';
import ContentLayout from '@layouts/ContentLayout';
import useAuthRole from '@hooks/useAuthRole';
import { LoadingState, NoDataState } from '@components/StateComponents';
import { MdEdit, MdOutlineCancel } from 'react-icons/md';
import { IoMdCheckmark } from 'react-icons/io';
import { ROLES } from '@constants';
import { getFxRatesConfig, updateFxRatesConfig, getVanceFxRates } from '@store/actions/fxServiceManagerActions';
import { CustomRatesCard, MarketRatesCard, GoogleRatesCard, VanceFxRatesCard } from './components';
import { setAlert } from '@store/actions/alertActions';

const FxServiceDashboard = () => {
  const dispatch = useDispatch();
  const { hasAnyAuthority } = useAuthRole();
  const canEdit = hasAnyAuthority(ROLES.FX_SERVICE_MANAGER_ROLE);

  const { isLoading, data: configData } = useSelector(
    (state) => state.fxServiceManager.fxRatesConfig
  );

  const { isLoading: isLoadingVanceFxRates, data: vanceFxRatesData } = useSelector(
    (state) => state.fxServiceManager.vanceFxRates
  );

  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    dispatch(getFxRatesConfig());
    dispatch(getVanceFxRates());
  }, [dispatch]);

  const handleRefreshVanceFxRates = () => {
    dispatch(getVanceFxRates());
  };

  useEffect(() => {
    if (configData) {
      setFormData(JSON.parse(JSON.stringify(configData)));
    }
  }, [configData]);

  const handleEditMode = () => setIsEditMode(true);

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setHasUnsavedChanges(false);
    if (configData) {
      setFormData(JSON.parse(JSON.stringify(configData)));
    }
  };

  const handleSave = () => {
    setIsSaving(true);

    dispatch(
      updateFxRatesConfig({
        payload: {
          feature_name: 'vance_fx_rates_config',
          feature_value: formData
        },
        onSuccess: () => {
          setIsEditMode(false);
          setHasUnsavedChanges(false);
        },
        onComplete: () => setIsSaving(false)
      })
    );
  };

  const handleChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleCurrencyChange = (section, currency, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        currencies: {
          ...prev[section]?.currencies,
          [currency]: {
            ...prev[section]?.currencies?.[currency],
            [field]: value
          }
        }
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleAddCurrency = (section, currencyCode) => {
    if (!currencyCode?.trim()) return;

    const upperCaseCurrency = currencyCode.toUpperCase().trim();

    if (formData?.[section]?.currencies?.[upperCaseCurrency]) {
      setAlert(`Currency ${upperCaseCurrency} already exists`);
      return;
    }

    const defaultValues = section === 'marketRates'
      ? { forceEnabled: false, allowedDeviationLowerBound: 0, allowedDeviationUpperBound: 0 }
      : { enabled: false, fxRate: 0 };

    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        currencies: {
          ...prev[section]?.currencies,
          [upperCaseCurrency]: defaultValues
        }
      }
    }));
    setHasUnsavedChanges(true);
  };

  if (isLoading && !formData) {
    return (
      <ContentLayout>
        <LoadingState />
      </ContentLayout>
    );
  }

  if (!formData && !isLoading) {
    return (
      <ContentLayout>
        <NoDataState message="No FX Rates configuration found" />
      </ContentLayout>
    );
  }

  return (
    <ContentLayout>
      <Grid gridTemplateColumns={{ base: '1fr', lg: '1fr' }}>
        <VStack
          overflowY="auto"
          overflowX="hidden"
          h={{ lg: '90vh' }}
          pt={10}
          pb={4}
          px={{ base: 4, sm: 6, lg: 10 }}
          alignItems="stretch"
          gap={6}
        >
          {/* Header */}
          <HStack justify="space-between" align="center">
            <Heading size="lg" color="colorPrimary">
              FX Service Dashboard
            </Heading>
            {canEdit && (
              <HStack>
                {hasUnsavedChanges && (
                  <Badge colorScheme="yellow" fontSize="sm" px={3} py={1}>
                    Unsaved Changes
                  </Badge>
                )}
                {!isEditMode ? (
                  <Button
                    colorScheme="brand"
                    size="md"
                    bg="brand.600"
                    _hover={{ bg: 'brand.700' }}
                    onClick={handleEditMode}
                    leftIcon={<MdEdit size="1.2rem" />}
                  >
                    Edit
                  </Button>
                ) : (
                  <HStack spacing={2}>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      size="md"
                      bg="red.900"
                      borderColor="red.500"
                      _hover={{ bg: 'red.800' }}
                      onClick={handleCancelEdit}
                      leftIcon={<MdOutlineCancel size="1.2rem" />}
                    >
                      Cancel
                    </Button>
                    <Button
                      colorScheme="brand"
                      size="md"
                      bg="brand.600"
                      _hover={{ bg: 'brand.700' }}
                      onClick={handleSave}
                      isLoading={isSaving}
                      leftIcon={<IoMdCheckmark size="1.2rem" />}
                    >
                      Save
                    </Button>
                  </HStack>
                )}
              </HStack>
            )}
          </HStack>
          {/* Vance FX Rates Table - Full Width */}
          <VanceFxRatesCard
            data={vanceFxRatesData}
            isLoading={isLoadingVanceFxRates}
            onRefresh={handleRefreshVanceFxRates}
          />
          {/* Cards Layout */}
          <Grid
            templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }}
            gap={3}
            w="full"
          >
            {/* Left Side - Market Rates */}
            {formData?.marketRates && (
              <MarketRatesCard
                data={formData.marketRates}
                isEditMode={isEditMode && canEdit}
                onChange={handleChange}
                onCurrencyChange={handleCurrencyChange}
                onAddCurrency={handleAddCurrency}
              />
            )}

            {/* Right Side - Custom Rates and Google Rates Stacked */}
            <VStack alignItems="stretch" gap={3}>
              {formData?.customRates && (
                <CustomRatesCard
                  data={formData.customRates}
                  isEditMode={isEditMode && canEdit}
                  onChange={handleChange}
                  onCurrencyChange={handleCurrencyChange}
                  onAddCurrency={handleAddCurrency}
                />
              )}

              {formData?.googleRates && (
                <GoogleRatesCard
                  data={formData.googleRates}
                  isEditMode={isEditMode && canEdit}
                  onChange={handleChange}
                />
              )}
            </VStack>
          </Grid>
        </VStack>
      </Grid>
    </ContentLayout>
  );
};

export default FxServiceDashboard;
